import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SafetyPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <section className="pt-20 md:pt-24 pb-8 md:pb-12 bg-primary-900">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <a href="/" className="text-xs text-primary-300 hover:text-white transition-colors flex items-center gap-1">
                <i className="ri-home-line w-3 h-3 flex items-center justify-center" /> Home
              </a>
              <span className="text-primary-400">/</span>
              <span className="text-xs text-primary-200 font-medium">Safety</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              CSSBuy <span className="text-primary-400">Safety</span> Guide
            </h1>
            <p className="text-primary-200 text-sm md:text-base leading-relaxed max-w-2xl">
              Protect yourself when using buying agents and online marketplaces. Learn about scams, fake links, payment safety, and best practices.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full py-12 md:py-16">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8 lg:p-10">
              <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                <p className="mb-4">
                  This <strong>CSSBuy Safety Guide</strong> is designed to help you protect yourself when using buying agents and making international purchases. This is an independent informational resource and does not represent official CSSBuy advice.
                </p>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">Is CSSBuy Legit?</h2>
                <p className="mb-4">
                  CSSBuy operates as a legitimate buying agent service. However, legitimacy does not eliminate all risks. When using any international purchasing service, you should be aware of potential issues including shipping delays, quality disputes, and communication barriers. Always verify current policies directly with the platform.
                </p>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">Common Risks to Watch For</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-semibold text-sm text-red-800 mb-2 flex items-center gap-2">
                      <i className="ri-link-m w-4 h-4 flex items-center justify-center" /> Fake Links
                    </h4>
                    <p className="text-xs text-red-700">Always verify URLs before clicking. Scammers may create fake login pages or product listings that mimic legitimate sites.</p>
                  </div>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-semibold text-sm text-amber-800 mb-2 flex items-center gap-2">
                      <i className="ri-coupon-line w-4 h-4 flex items-center justify-center" /> Fake Coupons
                    </h4>
                    <p className="text-xs text-amber-700">Be cautious of coupon codes from unofficial sources. Fake discounts may be used to steal account information.</p>
                  </div>
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <h4 className="font-semibold text-sm text-orange-800 mb-2 flex items-center gap-2">
                      <i className="ri-money-cny-circle-line w-4 h-4 flex items-center justify-center" /> Payment Risks
                    </h4>
                    <p className="text-xs text-orange-700">Never share banking details through chat. Use secure, traceable payment methods with buyer protection.</p>
                  </div>
                  <div className="p-4 bg-foreground-50 border border-foreground-200 rounded-lg">
                    <h4 className="font-semibold text-sm text-foreground-800 mb-2 flex items-center gap-2">
                      <i className="ri-user-settings-line w-4 h-4 flex items-center justify-center" /> Account Security
                    </h4>
                    <p className="text-xs text-foreground-700">Use strong, unique passwords and enable two-factor authentication whenever possible.</p>
                  </div>
                </div>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">Safe Buying Checklist</h2>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-600 text-sm" />
                    </div>
                    <p className="text-sm text-foreground-600">Verify the official website URL before logging in or entering payment information.</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-600 text-sm" />
                    </div>
                    <p className="text-sm text-foreground-600">Start with small test orders to evaluate service quality before large purchases.</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-600 text-sm" />
                    </div>
                    <p className="text-sm text-foreground-600">Request and carefully review QC photos before approving international shipping.</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-600 text-sm" />
                    </div>
                    <p className="text-sm text-foreground-600">Use payment methods with buyer protection, such as PayPal or credit cards.</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-600 text-sm" />
                    </div>
                    <p className="text-sm text-foreground-600">Read and understand refund, return, and dispute policies before ordering.</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-600 text-sm" />
                    </div>
                    <p className="text-sm text-foreground-600">Keep records of all transactions, communications, and shipping tracking numbers.</p>
                  </li>
                </ul>
                <div className="p-5 bg-primary-50 border border-primary-200 rounded-xl mt-8">
                  <p className="text-sm text-primary-800 font-medium mb-3">Learn more about safe buying</p>
                  <a href="/cssbuy-spreadsheet/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors mr-3">
                    CSSBuy Spreadsheet Guide
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}